import {
  type ParticipantScheduleResponse,
  type FieldData,
  type Field,
  type RenderContext,
  type Section,
  type FieldValue,
  CalculationsDataExt,
  FieldDataExt,
} from '@castoredc/castor_prose';

import { isBrandPointer } from '@hub/common';
import { createSlice } from '@reduxjs/toolkit';
import { formatInTimeZone } from 'date-fns-tz';
import {
  defaultContextDataJson,
  defaultDslSource,
  defaultFieldsDataJson,
  defaultStyling,
  defaultTranslationsJson,
} from 'features/admin/protocol-editor/components/AdminStudyEditor.utils';
import {
  LS_CONTEXT_DATA_JSON_KEY,
  LS_DSL_SOURCE_KEY,
  LS_FIELDS_DATA_JSON_KEY,
  LS_STYLING_CSS_KEY,
  LS_BRAND_KEY,
  LS_TRANSLATIONS_KEY,
} from 'features/admin/protocol-editor/constants';
import { updateFieldData } from 'features/questionnaires/logic/fieldsData';
import { readJsonFromLocalStorage } from 'features/store/localStorage';
import { getTimezoneName } from 'shared/utils/dateUtils';
import type { BrandPointer, DbCalculationsData } from '@hub/common';
import type { PayloadAction } from '@reduxjs/toolkit';
import type {
  QuestionnaireI18nKeys,
  ProseRenderOutput,
} from 'features/questionnaires/services/proseService';

type AdminInputError = {
  summary: string;
  details: string;
  line: number | undefined;
};

export type AdminProtocolEditorState = {
  i18nBaseKey: string | undefined;
  i18nKeys: QuestionnaireI18nKeys | undefined;
  fieldsDefinitions: Field[];
  fieldsData: FieldData;
  calculationsData: DbCalculationsData;
  pages: Section[];
  pageIndex: number;
  outputTabIndex: number;
  contextData: RenderContext | null;
  questionnaireName: string | undefined;
  availableQuestionnairesNames: string[];
  destinations: string[];
  scheduleName: string | undefined;
  availableSchedulesNames: string[];
  scheduleDate: string;
  scheduleTimezone: string;
  scheduleTasks: {
    id: string;
    beginningFormattedInTimezone: string;
    beginningIsoUtc: string;
    endFormattedInTimezone: string;
    endIsoUtc: string;
  }[];
  rawScheduleOutput: unknown;
  highlightedFieldName: string | undefined;
  // NOTE: Currently parsing and rendering are not separated steps available by PROSE API,
  // so the dlsSourceError can have error due to invalid syntax of DSL or semantic error such
  // as missing localTime property in context data
  dslSourceError: AdminInputError | undefined;
  scheduleError: string | undefined;
  fieldsDataError: AdminInputError | undefined;
  contextDataError: AdminInputError | undefined;
  translationsError: AdminInputError | undefined;
  dslSource: string;
  fieldsDataJson: string;
  contextDataJson: string;
  stylingCss: string;
  // Brand tab choice; publish writes it to manifest.brand.
  brand: BrandPointer | undefined;
  translationsJsons: Record<string, string>;
  languagesCodesCsv: string;
  activeLanguageCode: string;
  languagesCodesError: string | undefined;
};

const readStoredBrand = (): BrandPointer | undefined => {
  const value = readJsonFromLocalStorage<unknown>(LS_BRAND_KEY, undefined);
  return isBrandPointer(value) ? value : undefined;
};

const getLocalStorageValues = () => {
  const translationsJsons = readJsonFromLocalStorage<Record<string, string>>(
    LS_TRANSLATIONS_KEY,
    { 'en-US': defaultTranslationsJson }
  );

  return {
    dslSource: localStorage.getItem(LS_DSL_SOURCE_KEY) ?? defaultDslSource,
    fieldsDataJson:
      localStorage.getItem(LS_FIELDS_DATA_JSON_KEY) ?? defaultFieldsDataJson,
    contextDataJson:
      localStorage.getItem(LS_CONTEXT_DATA_JSON_KEY) ?? defaultContextDataJson,
    stylingCss: localStorage.getItem(LS_STYLING_CSS_KEY) ?? defaultStyling,
    brand: readStoredBrand(),
    translationsJsons,
    languagesCodesCsv: Object.keys(translationsJsons).join(',') || 'en-US',
    activeLanguageCode: Object.keys(translationsJsons)[0] ?? 'en-US',
  };
};

const initialState: AdminProtocolEditorState = {
  i18nBaseKey: undefined,
  i18nKeys: undefined,
  fieldsDefinitions: [],
  fieldsData: FieldDataExt.new(),
  calculationsData: CalculationsDataExt.new(),
  pages: [],
  pageIndex: 0,
  outputTabIndex: 0,
  contextData: null,
  questionnaireName: undefined,
  availableQuestionnairesNames: [],
  destinations: [],
  scheduleName: undefined,
  availableSchedulesNames: [],
  scheduleDate: new Date().toISOString(),
  scheduleTimezone: getTimezoneName(),
  scheduleTasks: [],
  rawScheduleOutput: undefined,
  highlightedFieldName: undefined,
  dslSourceError: undefined,
  scheduleError: undefined,
  fieldsDataError: undefined,
  contextDataError: undefined,
  translationsError: undefined,
  languagesCodesError: undefined,
  ...getLocalStorageValues(),
};

const adminProtocolEditorSlice = createSlice({
  name: 'adminProtocolEditor',
  initialState,
  reducers: {
    adminRestoreInitialState: () => {
      // Read fresh values from localStorage instead of using cached initialState
      // (without it stale values might be loaded if in-app routing was used
      // instead of full page reload)
      const localStorageValues = getLocalStorageValues();
      return {
        ...initialState,
        scheduleDate: new Date().toISOString(),
        scheduleTimezone: getTimezoneName(),
        ...localStorageValues,
      };
    },
    adminSetFieldDataFromInput: (
      state,
      action: PayloadAction<{
        fieldName: string;
        fieldValue: FieldValue | undefined;
      }>
    ) => {
      state.fieldsData = updateFieldData(
        state.fieldsData,
        action.payload.fieldName,
        action.payload.fieldValue
      );
    },
    adminApplyProseRenderOutput: (
      state,
      action: PayloadAction<
        Omit<ProseRenderOutput, 'contextData'> & {
          contextData: RenderContext | null;
        }
      >
    ) => {
      state.i18nBaseKey = action.payload.i18nBaseKey;
      state.i18nKeys = action.payload.i18nKeys;
      state.fieldsData = action.payload.fieldsData;
      state.calculationsData = action.payload.calculationsData;
      state.fieldsDefinitions = action.payload.fieldsDefinitions;
      state.pages = action.payload.fieldsSections;
      state.contextData = action.payload.contextData;

      if (action.payload.fieldsSections.length > 0) {
        state.pageIndex = Math.min(
          state.pageIndex,
          action.payload.fieldsSections.length - 1
        );
      }

      state.dslSourceError = undefined;
    },
    adminApplyProseScheduleOutput: (
      state,
      action: PayloadAction<ParticipantScheduleResponse | string>
    ) => {
      state.rawScheduleOutput = action.payload;

      if (typeof action.payload === 'object') {
        const activities = state.questionnaireName
          ? action.payload.activities.filter(
              a => a.kind?.kind?.value.questionnaire === state.questionnaireName
            )
          : action.payload.activities;

        const getInTimezone = (seconds: number) =>
          formatInTimeZone(
            seconds * 1000,
            state.scheduleTimezone,
            'EEE, dd-MMM-yyyy HH:mm'
          );

        const getInUtc = (seconds: number) =>
          new Date(seconds * 1000).toISOString();

        state.scheduleTasks = activities.flatMap(a =>
          a.tasks.map(t => ({
            id: crypto.randomUUID(),
            beginningFormattedInTimezone: t.beginning
              ? getInTimezone(t.beginning.seconds)
              : '',
            beginningIsoUtc: t.beginning ? getInUtc(t.beginning.seconds) : '',
            endFormattedInTimezone: t.end ? getInTimezone(t.end.seconds) : '',
            endIsoUtc: t.end ? getInUtc(t.end.seconds) : '',
          }))
        );

        state.scheduleError = undefined;
      } else {
        state.scheduleTasks = [];
        state.scheduleError = action.payload;
      }
    },
    adminSetAvailableQuestionnairesNames: (
      state,
      action: PayloadAction<string[]>
    ) => {
      state.availableQuestionnairesNames = action.payload;
    },
    adminSetDestinations: (state, action: PayloadAction<string[]>) => {
      state.destinations = action.payload;
    },
    adminSetQuestionnaireName: (
      state,
      action: PayloadAction<string | undefined>
    ) => {
      state.questionnaireName = action.payload;
      // Reset context so the next render uses start_edit_with_preview
      state.contextData = null;
    },
    adminSetAvailableSchedulesNames: (
      state,
      action: PayloadAction<string[]>
    ) => {
      state.availableSchedulesNames = action.payload;
    },
    adminSetScheduleName: (
      state,
      action: PayloadAction<string | undefined>
    ) => {
      state.scheduleName = action.payload;
    },
    adminSetScheduleDate: (state, action: PayloadAction<string>) => {
      state.scheduleDate = action.payload;
    },
    adminSetScheduleTimezone: (state, action: PayloadAction<string>) => {
      state.scheduleTimezone = action.payload;
    },
    adminSetDslSourceError: (state, action: PayloadAction<AdminInputError>) => {
      state.dslSourceError = action.payload;
    },
    adminSetFieldsDataError: (
      state,
      action: PayloadAction<AdminInputError>
    ) => {
      state.fieldsDataError = action.payload;
    },
    adminClearFieldsDataError: state => {
      state.fieldsDataError = undefined;
    },
    adminSetContextDataError: (
      state,
      action: PayloadAction<AdminInputError>
    ) => {
      state.contextDataError = action.payload;
    },
    adminClearContextDataError: state => {
      state.contextDataError = undefined;
    },
    adminSetTranslationsError: (
      state,
      action: PayloadAction<AdminInputError>
    ) => {
      state.translationsError = action.payload;
    },
    adminClearTranslationsError: state => {
      state.translationsError = undefined;
    },
    adminSetScheduleError: (state, action: PayloadAction<string>) => {
      state.scheduleError = action.payload;
    },
    adminSetPage: (state, action: PayloadAction<number>) => {
      state.pageIndex = action.payload;
    },
    adminSetOutputTabIndex: (state, action: PayloadAction<number>) => {
      state.outputTabIndex = action.payload;
    },
    adminSetHighlightedFieldName: (
      state,
      action: PayloadAction<string | undefined>
    ) => {
      state.highlightedFieldName = action.payload;
    },
    adminSetDslSource: (state, action: PayloadAction<string>) => {
      state.dslSource = action.payload;
      // Reset context so the next render uses start_edit_with_preview
      state.contextData = null;
    },
    adminSetFieldsDataJson: (state, action: PayloadAction<string>) => {
      state.fieldsDataJson = action.payload;
    },
    adminSetContextDataJson: (state, action: PayloadAction<string>) => {
      state.contextDataJson = action.payload;
    },
    adminSetStylingCss: (state, action: PayloadAction<string>) => {
      state.stylingCss = action.payload;
    },
    adminSetBrand: (state, action: PayloadAction<BrandPointer | undefined>) => {
      state.brand = action.payload;
    },
    adminSetTranslationForLanguage: (
      state,
      action: PayloadAction<{ languageCode: string; json: string }>
    ) => {
      state.translationsJsons[action.payload.languageCode] =
        action.payload.json;
    },
    adminSetLanguagesCodesCsv: (state, action: PayloadAction<string>) => {
      state.languagesCodesCsv = action.payload;
    },
    adminSetActiveLanguageCode: (state, action: PayloadAction<string>) => {
      state.activeLanguageCode = action.payload;
    },
    adminSetLanguagesCodesError: (state, action: PayloadAction<string>) => {
      state.languagesCodesError = action.payload;
    },
    adminClearLanguagesCodesError: state => {
      state.languagesCodesError = undefined;
    },
    adminApplyImportedTranslations: (
      state,
      action: PayloadAction<Record<string, string>>
    ) => {
      const languageCodes = Object.keys(action.payload).sort();

      if (languageCodes.length > 0) {
        state.translationsJsons = {};
        for (const [code, content] of Object.entries(action.payload)) {
          state.translationsJsons[code] = content;
        }
        state.languagesCodesCsv = languageCodes.join(', ');
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion -- length is checked
        state.activeLanguageCode = languageCodes[0]!;
        state.languagesCodesError = undefined;
      }
    },
    adminClearAllTranslations: state => {
      for (const code of Object.keys(state.translationsJsons)) {
        state.translationsJsons[code] = '{}';
      }
    },
  },
});

export const {
  adminRestoreInitialState,
  adminApplyProseRenderOutput,
  adminApplyProseScheduleOutput,
  adminSetAvailableQuestionnairesNames,
  adminSetDestinations,
  adminSetQuestionnaireName,
  adminSetAvailableSchedulesNames,
  adminSetScheduleName,
  adminSetScheduleDate,
  adminSetScheduleTimezone,
  adminSetDslSourceError,
  adminSetScheduleError,
  adminSetFieldsDataError,
  adminClearFieldsDataError,
  adminSetContextDataError,
  adminClearContextDataError,
  adminSetTranslationsError,
  adminClearTranslationsError,
  adminSetPage,
  adminSetOutputTabIndex,
  adminSetHighlightedFieldName,
  adminSetFieldDataFromInput,
  adminSetDslSource,
  adminSetFieldsDataJson,
  adminSetContextDataJson,
  adminSetStylingCss,
  adminSetBrand,
  adminSetTranslationForLanguage,
  adminSetLanguagesCodesCsv,
  adminSetActiveLanguageCode,
  adminSetLanguagesCodesError,
  adminClearLanguagesCodesError,
  adminApplyImportedTranslations,
  adminClearAllTranslations,
} = adminProtocolEditorSlice.actions;

export const adminProtocolEditorReducer = adminProtocolEditorSlice.reducer;
